import { Side } from "../components/SideMenu/side";
import Constants from "../constants";
import { SelectionHighlightData } from "../models/selectionHightlightData";

export type MaskMetadata = { [key: string]: { [key: string]: string } };

export const extractMaskData = (mask: HTMLImageElement) => {
    // Create a temporary canvas to draw the mask image and get the pixel data
    const maskCanvas = document.createElement("canvas");
    maskCanvas.width = mask.width;
    maskCanvas.height = mask.height;
    const maskContext = maskCanvas.getContext("2d");
    if (maskContext) {
        maskContext.drawImage(mask, 0, 0);
        const allPixels = maskContext.getImageData(0, 0, mask.width, mask.height).data;

        // Copy and use only one channel as the mask is a grayscale image
        const imageLength = mask.width * mask.height;
        let oneChannelPixels;
        if (typeof Uint8ClampedArray !== "undefined") {
            oneChannelPixels = new Uint8ClampedArray(imageLength);
        } else {
            oneChannelPixels = new Array(imageLength);
        }

        for (let pOrg = 0, pNew = 0; pNew < imageLength; pOrg += 4, pNew += 1) {
            oneChannelPixels[pNew] = allPixels[pOrg];
        }

        return oneChannelPixels;
    }

    return {};
};

export const extractMaskMetadata = (maskSources: MaskMetadata) => {
    const maskMetadata: MaskMetadata = {};
    Object.entries(maskSources).forEach(entry => {
        const [color, value] = entry;
        if (color.length === 8) {
            try {
                const redValue = parseInt(color.substring(2, 4), 16);
                maskMetadata[redValue] = value;
            } catch (e) {
                throw new Error("LoadMaskMetadata: Error: Invalid color in mask metadata");
            }
        }
    });

    return maskMetadata;
};

export const getMenuItemPixelValue = (menuId: string, maskMetadata: any) => {
    const pixelValue =
        Object.entries(maskMetadata).find(
            (metadata: Array<any>) => metadata[1].MENU_PATH && metadata[1].MENU_PATH?.startsWith(menuId)
        )?.[0] || "";

    return parseInt(pixelValue, 10) || -1;
};

export const getMenuPosition = (
    maskData: any,
    value: Number,
    maskHeight: number | undefined,
    maskWidth: number | undefined,
    currentSide: Side
) => {
    if (!maskHeight || !maskWidth || value === -1) {
        return Side.Right;
    }

    // Check how much the menu overlaps the image.
    const maskScale = maskHeight / Math.max(window.innerHeight, 1);
    const windowWidth = window.innerWidth;
    const imageWidth = maskWidth / maskScale;
    const imageEdgeDistance = windowWidth / 2 - imageWidth / 2;
    const fullMenuOverlap = Constants.sideMenuWidth - imageEdgeDistance;
    const menuOverlap = Math.round(maskScale * fullMenuOverlap);
    if (menuOverlap <= 0) return currentSide;

    const maskOverflow = Math.max(Math.round((maskWidth - maskScale * windowWidth) / 2), 0);

    // Count the pixels of the item that would be overlapped by the menu for each side.
    let leftOverlap = 0;
    let rightOverlap = 0;
    let total = 0;

    // Don't count parts outside the window.
    const xStart = maskOverflow;
    const xEnd = maskWidth - 2 * maskOverflow;

    const leftLimit = menuOverlap;
    const rightLimit = maskWidth - menuOverlap;
    for (let y = 0; y < maskHeight; y += 1) {
        for (let x = xStart; x < xEnd; x += 1) {
            const isSameItem = maskData[y * maskWidth + x] === value;
            if (isSameItem) {
                total += 1;
                if (x < leftLimit) {
                    leftOverlap += 1;
                } else if (x > rightLimit) {
                    rightOverlap += 1;
                }
            }
        }
    }

    // Put the menu at the side with the least overlap, but if the difference is
    // less than 5% of the item, keep it at the same side.
    if (Math.abs(leftOverlap - rightOverlap) < total * 0.05) return currentSide;
    return leftOverlap > rightOverlap ? Side.Right : Side.Left;
};

export const getMaskValueForMouseEvent = (
    mouseEvent: any,
    mask: HTMLImageElement | null,
    maskData: any
): number | null => {
    if (!mask || !maskData) return null;
    const ratioX = mouseEvent.nativeEvent.offsetX / mouseEvent.nativeEvent.target.offsetWidth;
    const ratioY = mouseEvent.nativeEvent.offsetY / mouseEvent.nativeEvent.target.offsetHeight;
    const maskX = Math.floor(ratioX * mask.width);
    const maskY = Math.floor(ratioY * mask.height);
    const pixelIndex = maskY * mask.width + maskX;
    return maskData[pixelIndex] || null;
};

export const getMaskMetadataForMouseEvent = (
    mouseEvent: any,
    mask: HTMLImageElement | null,
    maskData: any,
    maskMetadata: MaskMetadata | undefined
) => {
    if (!maskMetadata) return null;
    const colorValue = getMaskValueForMouseEvent(mouseEvent, mask, maskData);
    if (!colorValue) return null;
    return maskMetadata?.[colorValue] || null;
};

export const buildSelectionHighlightData = (
    maskData: any,
    mask: HTMLImageElement | null,
    configImage: HTMLImageElement | null,
    selectedValues: number[]
): SelectionHighlightData | undefined => {
    if (!maskData || !mask || !configImage) return undefined;
    return {
        maskData,
        configImageRect: {
            x: configImage.offsetLeft,
            y: configImage.offsetTop,
            width: configImage.width,
            height: configImage.height,
        },
        maskWidth: mask.width,
        maskHeight: mask.height,
        selectedValues,
    };
};
